import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { useAcceptConnection } from "@/react/hooks/connections/useAcceptConnection";
import { useIgnoreConnection } from "@/react/hooks/connections/useIgnoreConnection";
import { Button } from "@circle-react-uikit/Button";
import { generateInvalidateMemberQueryKey } from "./util";

interface RespondButtonsProps {
  connection: any;
  member: any;
}

export const RespondButtons = ({ member, connection }: RespondButtonsProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: acceptConnection, isLoading: isAccepting } =
    useAcceptConnection({
      onSuccess: () => {
        void queryClient.invalidateQueries(
          generateInvalidateMemberQueryKey(member.public_uid),
        );
        toast.success(
          t("profile_drawer.connect_banner.messages.connection_accepted", {
            memberName: member.name,
          }),
        );
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_accept_error"),
        );
      },
    });

  const { mutate: ignoreConnection, isLoading: isIgnoring } =
    useIgnoreConnection({
      onSuccess: () => {
        void queryClient.invalidateQueries(
          generateInvalidateMemberQueryKey(member.public_uid),
        );
        toast.success(
          t("profile_drawer.connect_banner.messages.connection_ignored"),
        );
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_ignore_error"),
        );
      },
    });

  const handleAcceptConnection = () => {
    acceptConnection(connection.id.toString());
  };

  const handleIgnoreConnection = () => {
    ignoreConnection(connection.id.toString());
  };

  return (
    <div className="flex gap-x-2">
      <Button
        variant="secondary"
        onClick={handleIgnoreConnection}
        disabled={isIgnoring || isAccepting}
      >
        {isIgnoring ? (
          <Icon type="loader" viewBox="0 0 16 16" />
        ) : (
          t("profile_drawer.connect_banner.states.ignore")
        )}
      </Button>
      <Button
        variant="primary"
        onClick={handleAcceptConnection}
        disabled={isAccepting || isIgnoring}
      >
        {isAccepting ? (
          <Icon type="loader" viewBox="0 0 16 16" />
        ) : (
          t("profile_drawer.connect_banner.states.accept")
        )}
      </Button>
    </div>
  );
};
